import { useState, useEffect, useCallback } from 'preact/hooks'
import type { CloudEmail, CloudContact } from '../../types'
import { listCloudEmails, listCloudContacts, deleteCloudContact, downloadBlobAsUrl } from '../../services/api'
import { formatDate, EmptyState, DetailRow, cardStyle, detailPanelStyle, listAreaStyle, refreshBtnStyle, tabStyle } from './shared'
import { ConfirmDialog, DeleteButton } from './CrudHelpers'
import { FullscreenImageViewer } from '../FullscreenImageViewer'
import {
  ComposeEmailDialog,
  buildReplyBody,
  buildForwardBody,
  replySubject,
  forwardSubject,
} from '../ComposeEmailDialog'

interface ComposePrefill {
  toEmail?: string
  toName?: string
  subject?: string
  body?: string
  /** When true, the To field is editable (reply/forward/new with picker). */
  toEditable?: boolean
}
type ComposeState = ComposePrefill | null

type EmailTab = 'inbox' | 'sent' | 'contacts'
type SortOrder = 'newest' | 'oldest'

// Flutter uploads contacts with camelCase keys; fall back to snake_case for any legacy rows.
const contactFriendlyName = (c: CloudContact) => c.friendlyName || c.friendly_name || ''
const contactFullName = (c: CloudContact) => c.fullName || c.full_name || ''
const contactExternalEmail = (c: CloudContact) => c.externalEmail || c.external_email || ''
const contactPiparEmail = (c: CloudContact) => c.piparEmail || c.pipar_email || ''
const contactInvitationStatus = (c: CloudContact) => c.invitationStatus || c.invitation_status || ''
const contactIsImportantSender = (c: CloudContact) => c.isImportantSender ?? c.is_important_sender ?? false
const contactIsBlocked = (c: CloudContact) => c.isBlocked ?? c.is_blocked ?? false
const contactIsPiparReceiver = (c: CloudContact) => c.isPiparReceiver ?? c.is_pipar_receiver ?? false
const contactCreatedAt = (c: CloudContact) => c.createdAt || c.created_at
const contactUpdatedAt = (c: CloudContact) => c.updatedAt || c.updated_at
const contactDisplayName = (c: CloudContact) => contactFriendlyName(c) || contactFullName(c) || contactExternalEmail(c) || 'Unknown'
const contactThumbKey = (c: CloudContact) =>
  (c.imageThumbnailKey as string | undefined) || (c.image_thumbnail_key as string | undefined)
const contactFullKey = (c: CloudContact) =>
  (c.imageBlobKey as string | undefined) || (c.image_blob_key as string | undefined)
const contactImageKey = (c: CloudContact) => contactThumbKey(c) || contactFullKey(c) || ''
const contactFullImageKey = (c: CloudContact) => contactFullKey(c) || contactThumbKey(c) || ''

const contactImageCache = new Map<string, string>()
async function loadContactImage(key: string): Promise<string | null> {
  if (!key) return null
  const cached = contactImageCache.get(key)
  if (cached) return cached
  const result = await downloadBlobAsUrl(key)
  if (result?.url) {
    contactImageCache.set(key, result.url)
    return result.url
  }
  return null
}

function ContactAvatar({ contact, size, preferFull, onZoom }: { contact: CloudContact; size: number; preferFull?: boolean; onZoom?: (url: string) => void }) {
  const key = preferFull ? contactFullImageKey(contact) : contactImageKey(contact)
  const [url, setUrl] = useState<string | null>(key ? contactImageCache.get(key) || null : null)

  useEffect(() => {
    let cancelled = false
    if (!key) { setUrl(null); return }
    if (contactImageCache.has(key)) {
      setUrl(contactImageCache.get(key) || null)
      return
    }
    loadContactImage(key).then((u) => { if (!cancelled) setUrl(u) })
    return () => { cancelled = true }
  }, [key])

  const initials = contactDisplayName(contact)
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?'

  const baseStyle: Record<string, string | number> = {
    width: size,
    height: size,
    minWidth: size,
    borderRadius: size / 2,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(45,165,160,0.18)',
    border: '1px solid rgba(250,250,250,0.1)',
    color: 'rgba(250,250,250,0.75)',
    fontSize: Math.max(10, Math.round(size * 0.4)),
    fontWeight: 600,
  }

  if (url) {
    const clickable = !!onZoom
    return (
      <div
        style={{ ...baseStyle, cursor: clickable ? 'zoom-in' : undefined }}
        onClick={clickable ? (e) => { e.stopPropagation(); onZoom!(url) } : undefined}
        title={clickable ? 'Click to view fullscreen' : undefined}
      >
        <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>
    )
  }
  return <div style={baseStyle}>{initials}</div>
}

export function EmailView() {
  const [emails, setEmails] = useState<CloudEmail[]>([])
  const [contacts, setContacts] = useState<CloudContact[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<EmailTab>('inbox')
  const [selectedEmail, setSelectedEmail] = useState<CloudEmail | null>(null)
  const [selectedContact, setSelectedContact] = useState<CloudContact | null>(null)
  const [confirmDeleteContact, setConfirmDeleteContact] = useState(false)
  const [sortOrder, setSortOrder] = useState<SortOrder>('newest')
  const [compose, setCompose] = useState<ComposeState>(null)
  const [zoomImageUrl, setZoomImageUrl] = useState<string | null>(null)

  const openReply = (email: CloudEmail) => {
    const senderDisplay = email.from_field || email.sender || ''
    const senderEmail = extractEmailAddress(email.from_field || email.sender || '') || email.sender || ''
    setCompose({
      toEmail: senderEmail,
      toName: senderDisplay,
      subject: replySubject(email.subject),
      body: buildReplyBody({
        senderDisplay,
        subject: email.subject,
        receivedAt: formatDate(email.received_at),
        bodyPlain: email.body_plain,
      }),
      toEditable: true,
    })
  }

  const openForward = (email: CloudEmail) => {
    const isInbound = email.direction === 'inbound'
    setCompose({
      subject: forwardSubject(email.subject),
      body: buildForwardBody({
        fromLabel: isInbound ? 'From' : 'To',
        fromValue: isInbound
          ? (email.from_field || email.sender || '')
          : (email.to_name ? `${email.to_name} <${email.to_email}>` : email.to_email || ''),
        subject: email.subject,
        date: formatDate(isInbound ? email.received_at : email.sent_at),
        body: isInbound ? email.body_plain : email.body,
      }),
      toEditable: true,
    })
  }

  const openSendToContact = (contact: CloudContact, target: 'pipar' | 'external') => {
    const to = target === 'pipar' ? contactPiparEmail(contact) : contactExternalEmail(contact)
    if (!to) return
    setCompose({
      toEmail: to,
      toName: contactDisplayName(contact),
      toEditable: false,
    })
  }

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [e, c] = await Promise.all([listCloudEmails(), listCloudContacts()])
      setEmails(e)
      setContacts(c)
    } catch (e) {
      console.error('Failed to load emails:', e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const emailTime = (e: CloudEmail): number => {
    const ts = e.direction === 'inbound' ? e.received_at : e.sent_at
    const n = ts ? Date.parse(ts) : NaN
    return isNaN(n) ? 0 : n
  }
  const sortEmails = (list: CloudEmail[]): CloudEmail[] => {
    const sign = sortOrder === 'newest' ? -1 : 1
    return [...list].sort((a, b) => sign * (emailTime(a) - emailTime(b)))
  }
  const inbound = sortEmails(emails.filter((e) => e.direction === 'inbound'))
  const sent = sortEmails(emails.filter((e) => e.direction === 'sent'))

  const handleSelectEmail = (email: CloudEmail) => {
    setSelectedEmail(selectedEmail?.id === email.id ? null : email)
    setSelectedContact(null)
  }

  const handleSelectContact = (contact: CloudContact) => {
    setSelectedContact(selectedContact?.id === contact.id ? null : contact)
    setSelectedEmail(null)
    setConfirmDeleteContact(false)
  }

  const handleDeleteContact = async () => {
    if (!selectedContact) return
    try {
      await deleteCloudContact(selectedContact.id)
      setContacts((prev) => prev.filter((c) => c.id !== selectedContact.id))
      setSelectedContact(null)
      setConfirmDeleteContact(false)
    } catch (e) {
      console.error('Failed to delete contact:', e)
    }
  }

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      {/* Email list */}
      <div style={{ ...listAreaStyle, maxWidth: 360, minWidth: 280 }}>
        <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid rgba(250,250,250,0.1)' }}>
          <button onClick={() => { setActiveTab('inbox'); setSelectedEmail(null); setSelectedContact(null) }} style={tabStyle(activeTab === 'inbox')}>
            Inbox ({inbound.length})
          </button>
          <button onClick={() => { setActiveTab('sent'); setSelectedEmail(null); setSelectedContact(null) }} style={tabStyle(activeTab === 'sent')}>
            Sent ({sent.length})
          </button>
          <button onClick={() => { setActiveTab('contacts'); setSelectedEmail(null); setSelectedContact(null) }} style={tabStyle(activeTab === 'contacts')}>
            Contacts ({contacts.length})
          </button>
          <span style={{ flex: 1 }} />
          {activeTab !== 'contacts' && (
            <>
              <button
                onClick={() => setCompose({ toEditable: true })}
                style={{ ...newBtnStyle, marginRight: 6 }}
                title="Compose new email"
              >
                + New
              </button>
              <button
                onClick={() => setSortOrder(sortOrder === 'newest' ? 'oldest' : 'newest')}
                style={{ ...refreshBtnStyle, marginRight: 6 }}
                title="Toggle sort order"
              >
                {sortOrder === 'newest' ? 'Newest \u2193' : 'Oldest \u2191'}
              </button>
            </>
          )}
          <button onClick={load} style={{ ...refreshBtnStyle, margin: '0 8px' }} disabled={loading}>
            {loading ? '...' : 'Refresh'}
          </button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '8px 16px' }}>
          {loading && <div style={{ padding: 24, textAlign: 'center', color: 'rgba(250,250,250,0.4)' }}>Loading...</div>}

          {!loading && activeTab === 'inbox' && (
            inbound.length === 0 ? <EmptyState label="No emails" /> : inbound.map((email) => (
              <EmailRow key={email.id} email={email} selected={selectedEmail?.id === email.id} onSelect={handleSelectEmail} />
            ))
          )}

          {!loading && activeTab === 'sent' && (
            sent.length === 0 ? <EmptyState label="No sent emails" /> : sent.map((email) => (
              <EmailRow key={email.id} email={email} selected={selectedEmail?.id === email.id} onSelect={handleSelectEmail} />
            ))
          )}

          {!loading && activeTab === 'contacts' && (
            contacts.length === 0 ? <EmptyState label="No contacts" /> : contacts.map((contact) => (
              <div
                key={contact.id}
                onClick={() => handleSelectContact(contact)}
                style={{
                  ...cardStyle,
                  cursor: 'pointer',
                  background: selectedContact?.id === contact.id ? 'rgba(45,165,160,0.15)' : cardStyle.background,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <ContactAvatar contact={contact} size={32} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontWeight: 500 }}>{contactDisplayName(contact)}</span>
                      {contactIsImportantSender(contact) && <span style={{ fontSize: 10, padding: '1px 4px', background: '#e65100', borderRadius: 2, color: '#fff' }}>Important</span>}
                      {contactIsBlocked(contact) && <span style={{ fontSize: 10, padding: '1px 4px', background: '#c62828', borderRadius: 2, color: '#fff' }}>Blocked</span>}
                    </div>
                    <div style={{ fontSize: 12, color: 'rgba(250,250,250,0.5)', marginTop: 4 }}>
                      {contactExternalEmail(contact)}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Detail panel */}
      {selectedEmail && (
        <div style={{ ...detailPanelStyle, width: 'auto', flex: 1 }}>
          <div style={{ padding: 16, borderBottom: '1px solid rgba(250,250,250,0.1)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ flex: 1, fontWeight: 600, fontSize: 16 }}>{selectedEmail.subject || '(no subject)'}</span>
            {selectedEmail.direction === 'inbound' && (
              <button
                onClick={() => openReply(selectedEmail)}
                style={actionBtnStyle}
                title="Reply to this email"
              >
                ↩ Reply
              </button>
            )}
            <button
              onClick={() => openForward(selectedEmail)}
              style={actionBtnStyle}
              title="Forward this email"
            >
              ↪ Forward
            </button>
            <button onClick={() => setSelectedEmail(null)} style={refreshBtnStyle}>X</button>
          </div>
          <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
            <DetailRow
              label="From"
              value={selectedEmail.direction === 'inbound'
                ? (selectedEmail.sender || selectedEmail.from_field)
                : selectedEmail.from_email}
            />
            <DetailRow
              label="To"
              value={selectedEmail.direction === 'inbound'
                ? selectedEmail.recipient
                : (selectedEmail.to_name ? `${selectedEmail.to_name} <${selectedEmail.to_email}>` : selectedEmail.to_email)}
            />
            <DetailRow
              label="Date"
              value={formatDate(selectedEmail.direction === 'inbound' ? selectedEmail.received_at : selectedEmail.sent_at)}
            />
            {selectedEmail.priority && selectedEmail.priority !== 'normal' && (
              <DetailRow label="Priority" value={selectedEmail.priority} />
            )}
            <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid rgba(250,250,250,0.1)' }}>
              {selectedEmail.direction === 'inbound' && selectedEmail.body_html ? (
                <div style={{ fontSize: 14, lineHeight: 1.6, maxHeight: '60vh', overflow: 'auto' }} dangerouslySetInnerHTML={{ __html: selectedEmail.body_html }} />
              ) : (
                <div style={{ fontSize: 14, whiteSpace: 'pre-wrap', lineHeight: 1.6, color: 'rgba(250,250,250,0.8)' }}>
                  {(selectedEmail.direction === 'inbound' ? selectedEmail.body_plain : selectedEmail.body) || '(empty)'}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedContact && (
        <div style={{ ...detailPanelStyle, width: 'auto', flex: 1 }}>
          <div style={{ padding: 16, borderBottom: '1px solid rgba(250,250,250,0.1)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ flex: 1, fontWeight: 600, fontSize: 16 }}>{contactDisplayName(selectedContact)}</span>
            {contactInvitationStatus(selectedContact) === 'accepted' && contactPiparEmail(selectedContact) && (
              <button
                onClick={() => openSendToContact(selectedContact, 'pipar')}
                style={actionBtnStyle}
                title="Send a PiPar message to this contact"
              >
                ✉ PiPar
              </button>
            )}
            {contactExternalEmail(selectedContact) && (
              <button
                onClick={() => openSendToContact(selectedContact, 'external')}
                style={actionBtnStyle}
                title="Send an email to this contact's external address"
              >
                ✉ Email
              </button>
            )}
            <DeleteButton onClick={() => setConfirmDeleteContact(true)} />
            <button onClick={() => setSelectedContact(null)} style={refreshBtnStyle}>X</button>
          </div>
          <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <ContactAvatar contact={selectedContact} size={200} preferFull onZoom={setZoomImageUrl} />
            </div>
            <DetailRow label="Name" value={contactFullName(selectedContact)} />
            <DetailRow label="Email" value={contactExternalEmail(selectedContact)} />
            <DetailRow label="PiPar Email" value={contactPiparEmail(selectedContact)} />
            <DetailRow label="Invitation" value={contactInvitationStatus(selectedContact)} />
            {contactIsImportantSender(selectedContact) && <DetailRow label="Important Sender" value="Yes" />}
            {contactIsBlocked(selectedContact) && <DetailRow label="Blocked" value="Yes" />}
            {contactIsPiparReceiver(selectedContact) && <DetailRow label="PiPar Receiver" value="Yes" />}
            <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px solid rgba(250,250,250,0.1)' }}>
              <DetailRow label="Created" value={formatDate(contactCreatedAt(selectedContact))} />
              <DetailRow label="Updated" value={formatDate(contactUpdatedAt(selectedContact))} />
            </div>
          </div>
        </div>
      )}

      {/* Delete contact confirm */}
      {confirmDeleteContact && selectedContact && (
        <ConfirmDialog
          message={`Delete contact "${contactDisplayName(selectedContact)}"?`}
          onConfirm={handleDeleteContact}
          onCancel={() => setConfirmDeleteContact(false)}
        />
      )}

      {/* Compose / Reply / Forward dialog */}
      {compose && (
        <ComposeEmailDialog
          initialToEmail={compose.toEmail}
          initialToName={compose.toName}
          initialSubject={compose.subject}
          initialBody={compose.body}
          recipientLocked={!compose.toEditable}
          onClose={(sent) => {
            setCompose(null)
            if (sent) load()
          }}
        />
      )}

      {zoomImageUrl && (
        <FullscreenImageViewer src={zoomImageUrl} onClose={() => setZoomImageUrl(null)} />
      )}
    </div>
  )
}

/** Extract bare email address from strings like `Name <email@x.com>`. */
function extractEmailAddress(s: string): string {
  if (!s) return ''
  const match = s.match(/<([^>]+)>/)
  if (match) return match[1].trim()
  if (s.includes('@')) return s.trim()
  return ''
}

const actionBtnStyle: Record<string, string | number> = {
  padding: '4px 10px',
  border: '1px solid rgba(45,165,160,0.4)',
  borderRadius: 6,
  background: 'rgba(45,165,160,0.15)',
  color: '#2DA5A0',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 500,
  whiteSpace: 'nowrap',
}

const newBtnStyle: Record<string, string | number> = {
  padding: '4px 12px',
  border: '1px solid rgba(45,165,160,0.5)',
  borderRadius: 6,
  background: 'rgba(45,165,160,0.2)',
  color: '#2DA5A0',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
}

function priorityMeta(priority: string | undefined | null): { symbol: string; color: string; label: string } | null {
  switch (priority) {
    case 'urgent': return { symbol: '\u2757', color: '#e53935', label: 'Urgent' }
    case 'high':   return { symbol: '\u2191', color: '#fb8c00', label: 'High' }
    case 'low':    return { symbol: '\u2193', color: '#9e9e9e', label: 'Low' }
    default: return null
  }
}

function PriorityBadge({ priority }: { priority: string | undefined | null }) {
  const meta = priorityMeta(priority)
  if (!meta) return null
  return (
    <span
      title={meta.label}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 16,
        height: 16,
        fontSize: 11,
        fontWeight: 700,
        color: meta.color,
        lineHeight: 1,
      }}
    >
      {meta.symbol}
    </span>
  )
}

function EmailRow({ email, selected, onSelect }: { email: CloudEmail; selected: boolean; onSelect: (e: CloudEmail) => void }) {
  const isInbound = email.direction === 'inbound'
  return (
    <div
      onClick={() => onSelect(email)}
      style={{
        ...cardStyle,
        cursor: 'pointer',
        borderLeft: `3px solid ${isInbound ? '#4fc3f7' : '#81c784'}`,
        background: selected ? 'rgba(45,165,160,0.15)' : cardStyle.background,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <PriorityBadge priority={email.priority} />
        <span style={{ flex: 1, fontWeight: 500 }}>{email.subject || '(no subject)'}</span>
        {isInbound && email.is_read === 0 && (
          <span style={{ fontSize: 10, padding: '1px 4px', background: '#1565c0', borderRadius: 2, color: '#fff' }}>Unread</span>
        )}
      </div>
      <div style={{ fontSize: 12, color: 'rgba(250,250,250,0.5)', marginTop: 4 }}>
        {isInbound
          ? `From: ${email.sender || email.from_field || 'Unknown'}`
          : `To: ${email.to_name || email.to_email || 'Unknown'}`}
      </div>
      <div style={{ fontSize: 11, color: 'rgba(250,250,250,0.35)', marginTop: 2 }}>
        {formatDate(isInbound ? email.received_at : email.sent_at)}
      </div>
    </div>
  )
}
